import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Edit2, Check, X } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

interface EditablePlanNameProps {
  planId: number;
  currentName: string;
  planNumber: number;
  onNameUpdate?: (newName: string) => void;
}

// Shape of the server response for a training plan
interface TrainingPlanResponse {
  id: number;
  userId: string;
  name: string;
  planData: unknown;
  userProfile?: unknown;
  createdAt: string;
}

export function EditablePlanName({ 
  planId, 
  currentName, 
  planNumber, 
  onNameUpdate 
}: EditablePlanNameProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [newName, setNewName] = useState("");
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Get the display name (custom name or default)
  const displayName = currentName || `Training Plan #${planNumber}`;

  const updateNameMutation = useMutation<TrainingPlanResponse, Error, string>({
    mutationFn: async (name: string) => {
      const res = await apiRequest('PATCH', `/api/plans/${planId}/name`, { name });
      if (!res.ok) {
        const text = await res.text().catch(() => null);
        throw new Error(text || "Failed to update plan name");
      }
      return (await res.json()) as TrainingPlanResponse;
    },
    onSuccess: (data) => {
      toast({
        title: "Plan name updated",
        description: "Your training plan name has been saved successfully.",
      });
      
      // Invalidate and refetch relevant plan queries
      queryClient.invalidateQueries({
        predicate: (q) => Array.isArray(q.queryKey) && typeof q.queryKey[0] === 'string' && (q.queryKey[0] as string).startsWith('/api/plans')
      });
      
      // Call callback if provided
      if (onNameUpdate) {
        onNameUpdate(data.name);
      }
      
      setIsEditing(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Error updating plan name",
        description: error.message || "Failed to update plan name. Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleStartEdit = () => {
    setNewName(currentName || `Training Plan #${planNumber}`);
    setIsEditing(true);
  };

  const handleSave = () => {
    if (newName.trim() && newName.trim() !== currentName) {
      updateNameMutation.mutate(newName.trim());
    } else {
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    setNewName("");
    setIsEditing(false);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  if (isEditing) {
    return (
      <div className="flex items-center space-x-2 min-w-0 flex-1">
        <Input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={handleKeyPress}
          className="text-xl font-bold bg-white/10 border-white/20 text-gray-800 dark:text-white rounded-xl"
          autoFocus
          maxLength={50}
        />
        <Button
          size="sm"
          onClick={handleSave}
          disabled={updateNameMutation.isPending || !newName.trim()}
          className="px-2 py-1 h-8 bg-gray-700 hover:bg-gray-800 text-white rounded-lg"
        >
          {updateNameMutation.isPending ? (
            <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <Check className="w-3 h-3" />
          )}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={handleCancel}
          disabled={updateNameMutation.isPending}
          className="px-2 py-1 h-8 text-gray-600 hover:text-gray-800 dark:text-gray-300 dark:hover:text-white rounded-lg"
        >
          <X className="w-3 h-3" />
        </Button>
      </div>
    );
  }

  return (
    <div className="flex items-center space-x-2 min-w-0 flex-1">
      <h3 className="text-xl font-bold text-gray-800 dark:text-white truncate">
        {displayName}
      </h3>
      <Button
        size="sm"
        variant="ghost"
        onClick={handleStartEdit}
        className="px-2 py-1 h-8 text-gray-500 hover:text-gray-700 dark:hover:text-gray-200 transition-colors rounded-lg opacity-0 group-hover:opacity-100"
      >
        <Edit2 className="w-3 h-3" />
      </Button>
    </div>
  );
}